import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { ArrowLeft, BookMarked, Users } from "lucide-react";

export default function Mercredis() {
  const [data, setData] = useState(null);
  const [periodKey, setPeriodKey] = useState("all");

  useEffect(() => {
    api.get("/direction/mercredis").then(({ data }) => setData(data));
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-[var(--cream)]">
        <AppHeader />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 text-[var(--ink-soft)]">Chargement...</div>
      </div>
    );
  }

  const period = data.periods.find((p) => p.key === periodKey);
  const rows = data.rows.filter((r) => !period || (r.date >= period.start && r.date <= period.end));
  const ratt = rows.filter((r) => r.type === "rattrapage");
  const etude = rows.filter((r) => r.type === "etude");
  const count = (list, status) => list.filter((r) => r.status === status).length;

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="mercredis-back">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <div className="mt-6 pb-8 border-b border-[var(--parchment-2)]">
          <div className="eyebrow">Direction · Suivi évolutif par semi-trimestre</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Les mercredis matin</h1>
          <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
            Élèves convoqués au rattrapage et inscrits à l'étude, avec leurs présences,
            retards et absences. Filtrez par semi-trimestre.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="semi-period-filter">
          <Chip active={periodKey === "all"} label="Année complète" onClick={() => setPeriodKey("all")} testId="semi-chip-all" />
          {data.periods.map((p) => (
            <Chip key={p.key} active={periodKey === p.key} label={p.name} sub={`${p.start} → ${p.end}`} onClick={() => setPeriodKey(p.key)} testId={`semi-chip-${p.key}`} />
          ))}
        </div>

        <div className="mt-8 grid sm:grid-cols-2 gap-6">
          <StatCard
            icon={<BookMarked size={18} />}
            title="Rattrapages"
            total={ratt.length}
            present={count(ratt, "present")}
            late={count(ratt, "late")}
            absent={count(ratt, "absent")}
            pending={ratt.filter((r) => !r.status).length}
            testId="stats-rattrapage"
          />
          <StatCard
            icon={<Users size={18} />}
            title="Étude simple"
            total={etude.length}
            present={count(etude, "present")}
            late={count(etude, "late")}
            absent={count(etude, "absent")}
            pending={etude.filter((r) => !r.status).length}
            testId="stats-etude"
          />
        </div>

        <div className="mt-8 bg-white border border-[var(--parchment-2)] p-6 overflow-x-auto">
          <table className="w-full text-sm" data-testid="mercredis-table">
            <thead>
              <tr className="border-b border-[var(--parchment-2)] text-left">
                <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Date</th>
                <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Élève</th>
                <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Classe</th>
                <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Type</th>
                <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Matière</th>
                <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Statut</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.type}-${r.id}`} className="border-b border-[var(--parchment-2)]/60" data-testid={`mercredis-row-${r.id}`}>
                  <td className="py-3 font-mono-spec text-xs text-[var(--muted)]">{r.date}</td>
                  <td className="py-3">{r.student?.last_name.toUpperCase()} {r.student?.first_name}</td>
                  <td className="py-3 text-[var(--ink-soft)]">{r.class?.name}</td>
                  <td className="py-3">
                    <span className={`px-2 py-0.5 text-xs font-medium ${r.type === "rattrapage" ? "bg-[var(--maroon)]/10 text-[var(--maroon)]" : "bg-[var(--parchment)] text-[var(--ink-soft)]"}`}>
                      {r.type === "rattrapage" ? "Rattrapage" : "Étude"}
                    </span>
                  </td>
                  <td className="py-3 text-[var(--ink-soft)]">{r.subject?.name || "—"}</td>
                  <td className="py-3 text-center"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-[var(--ink-soft)] italic">Aucune donnée sur cette période.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon, title, total, present, late, absent, pending, testId }) {
  return (
    <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-[var(--maroon)]">
        {icon}
        <h2 className="font-serif-display text-2xl font-semibold text-[var(--ink)]">{title}</h2>
        <span className="ml-auto font-serif-display text-3xl font-semibold">{total}</span>
      </div>
      <div className="mt-4 grid grid-cols-4 gap-3 text-center text-sm">
        <Mini label="Présents" value={present} color="var(--ok)" />
        <Mini label="Retards" value={late} color="var(--just)" />
        <Mini label="Absents" value={absent} color="var(--late)" />
        <Mini label="Non notés" value={pending} color="var(--muted)" />
      </div>
    </div>
  );
}

function Mini({ label, value, color }) {
  return (
    <div>
      <div className="font-mono-spec text-xl font-semibold" style={{ color }}>{value}</div>
      <div className="text-xs text-[var(--ink-soft)] mt-0.5">{label}</div>
    </div>
  );
}

const STATUS_BADGE_META = {
  present: { color: "var(--ok)", label: "Présent" },
  late: { color: "var(--just)", label: "Retard" },
  absent: { color: "var(--late)", label: "Absent" },
};

function StatusBadge({ status }) {
  if (!status) return <span className="text-xs text-[var(--muted)] italic">Non noté</span>;
  const meta = STATUS_BADGE_META[status] || STATUS_BADGE_META.absent;
  return <span className="px-2 py-0.5 text-xs font-medium text-white" style={{ background: meta.color }}>{meta.label}</span>;
}

function Chip({ active, label, sub, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={sub}
      className={`px-3 py-1.5 text-xs font-medium border transition ${
        active ? "bg-[var(--maroon)] text-white border-[var(--maroon)]" : "bg-white text-[var(--ink-soft)] border-[var(--parchment-2)] hover:border-[var(--ink)]"
      }`}
    >
      {label}
    </button>
  );
}
